import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import '../components/Register.css';
import '../components/Login.css';

const API_URL  = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_URL = `${API_URL}/authRoutes`;

const cleanPhone = (raw) => (raw || '').replace(/\D/g, '').slice(-10);
const RESEND_COOLDOWN = 30;

const QuickRegister = () => {
  const { loginWithOtp } = useContext(AuthContext);
  const navigate = useNavigate();

  // ── Step: 'form' | 'otp' ─────────────────────────────────────────────────
  const [step, setStep] = useState('form');

  // ── Form fields ───────────────────────────────────────────────────────────
  const [name, setName]       = useState('');
  const [phone, setPhone]     = useState('');
  const [email, setEmail]     = useState('');
  const [address, setAddress] = useState('');

  // ── OTP ───────────────────────────────────────────────────────────────────
  const [otpCode, setOtpCode]       = useState('');
  const [phoneHint, setPhoneHint]   = useState('');
  const [resendSecs, setResendSecs] = useState(0);
  const resendTimer = useRef(null);

  // ── Shared ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => () => clearInterval(resendTimer.current), []);

  const startResendTimer = () => {
    setResendSecs(RESEND_COOLDOWN);
    resendTimer.current = setInterval(() => {
      setResendSecs(s => {
        if (s <= 1) { clearInterval(resendTimer.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  // ── Form submit → create account + send OTP ───────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    const p = cleanPhone(phone);
    if (!name.trim())      return setError('Full name is required.');
    if (p.length !== 10)   return setError('Enter a valid 10-digit phone number.');
    if (!address.trim())   return setError('Delivery address is required.');
    if (!/\b\d{6}\b/.test(address)) return setError('Please include your 6-digit pincode in the address.');

    setLoading(true);
    try {
      const res = await axios.post(`${AUTH_URL}/quick-register`, {
        name: name.trim(),
        phone: p,
        email: email.trim() || undefined,
        address: address.trim(),
      });
      setPhoneHint(res.data.phone_hint);
      setStep('otp');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP verify → create account → login → checkout ───────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) return setError('Enter the 6-digit OTP.');
    setError(''); setLoading(true);
    try {
      await loginWithOtp(cleanPhone(phone), otpCode, '/quick-register/verify');
      navigate('/checkout', { replace: true });
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP — re-submit the form data to regenerate OTP ───────────────
  const handleResend = async () => {
    if (resendSecs > 0) return;
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${AUTH_URL}/quick-register`, {
        name: name.trim(),
        phone: cleanPhone(phone),
        email: email.trim() || undefined,
        address: address.trim(),
      });
      setPhoneHint(res.data.phone_hint);
      setOtpCode('');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="register-container">
      <div className="register-background">
        <div className="background-shape shape-1" />
        <div className="background-shape shape-2" />
        <div className="background-shape shape-3" />
      </div>

      <div className="register-card">
        <div className="register-header">
          <img src="/logo.webp" alt="The New Luxury" className="register-logo" />
        </div>

        {error && (
          <div className="register-error">
            <span>&#9888;</span>
            <span>{error}</span>
          </div>
        )}

        {/* ── Step 1: Registration form ── */}
        {step === 'form' && (
          <>
            <div className="register-welcome">
              <h2>Almost there!</h2>
              <p>Create your account to complete the order</p>
            </div>

            <form onSubmit={handleRegister} className="register-form" noValidate>
              <div className="form-group">
                <label htmlFor="qr-name">Full Name</label>
                <div className="input-wrapper">
                  <span className="input-icon">&#128100;</span>
                  <input
                    id="qr-name" type="text" value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required disabled={loading} autoComplete="name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="qr-phone">Phone Number</label>
                <div className="input-wrapper">
                  <span className="input-icon">&#128222;</span>
                  <input
                    id="qr-phone" type="tel" inputMode="numeric" value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="10-digit mobile number"
                    required disabled={loading} autoComplete="tel" maxLength={13}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="qr-email">
                  Email <span className="register-optional">(optional)</span>
                </label>
                <div className="input-wrapper">
                  <span className="input-icon">&#9993;</span>
                  <input
                    id="qr-email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Your email (optional)"
                    disabled={loading} autoComplete="email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="qr-address">
                  Delivery Address <span className="register-optional">(include pincode)</span>
                </label>
                <textarea
                  id="qr-address" value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="Full address including 6-digit pincode"
                  rows={3} required disabled={loading}
                  className="register-textarea"
                />
              </div>

              <button type="submit" className="register-btn login-button--wa" disabled={loading}>
                {loading
                  ? <><span className="spinner" /><span>Creating account…</span></>
                  : <><span className="wa-icon">&#128172;</span> Send OTP via WhatsApp</>}
              </button>
            </form>

            <div className="register-signin">
              Already have an account?{' '}
              <button className="register-signin-link" onClick={() => navigate('/login')}>
                Sign In
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: OTP verification ── */}
        {step === 'otp' && (
          <>
            <div className="register-welcome">
              <h2>Verify your number</h2>
              <p>OTP sent to <strong>{phoneHint}</strong> on WhatsApp</p>
            </div>

            <form onSubmit={handleVerify} className="register-form" noValidate>
              <div className="form-group">
                <label htmlFor="qr-otp">One-Time Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">&#128273;</span>
                  <input
                    id="qr-otp" type="text" inputMode="numeric" maxLength={6}
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit OTP"
                    required disabled={loading} autoFocus
                  />
                </div>
              </div>

              <button type="submit" className="register-btn login-button--wa"
                disabled={loading || otpCode.length !== 6}>
                {loading
                  ? <><span className="spinner" /><span>Verifying…</span></>
                  : 'Verify & Place Order'}
              </button>

              <div className="otp-resend-row" style={{ marginTop: 12 }}>
                <button type="button" className="fp-back-btn"
                  style={{ width: 'auto', marginTop: 0 }}
                  onClick={() => { setStep('form'); setOtpCode(''); setError(''); clearInterval(resendTimer.current); setResendSecs(0); }}>
                  ← Change details
                </button>
                <button
                  type="button"
                  className={`resend-btn${resendSecs > 0 ? ' resend-btn--waiting' : ''}`}
                  onClick={handleResend}
                  disabled={resendSecs > 0 || loading}
                >
                  {resendSecs > 0 ? `Resend in ${resendSecs}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          </>
        )}

        <div className="register-footer">
          <p>&#169; {new Date().getFullYear()} The New Luxury. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default QuickRegister;
